import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { formatViews } from "@/lib/video";

export default function AboutPage() {
  const totalViews = videos.reduce((sum, v) => sum + v.views, 0);

  const scopePoints = [
    { icon: "ri-leaf-line", title: "生活日常", desc: "收纳整理、旅行见闻、治愈系慢生活记录。" },
    { icon: "ri-restaurant-2-line", title: "美食探店", desc: "探店员试吃、家常菜谱与深夜食堂分享。" },
    { icon: "ri-music-2-line", title: "音乐现场", desc: "翻唱、原创与乐器演奏，用声音记录情绪。" },
    { icon: "ri-gamepad-line", title: "游戏集锦", desc: "精彩操作、新游试玩与趣味解说。" },
    { icon: "ri-emotion-laugh-line", title: "搞笑整活", desc: "沙雕日常与爆笑合集，专治不开心。" },
    { icon: "ri-video-line", title: "更多内容", desc: "持续收录最新热门短视频，每日更新。" },
  ];

  const steps = [
    { step: "01", title: "挑选分类", desc: "在首页点击分类快捷入口，或从顶部导航进入感兴趣的分类列表页。" },
    { step: "02", title: "筛选内容", desc: "按上传时间、视频时长和排序方式筛选，快速锁定你想看的内容。" },
    { step: "03", title: "开始观看", desc: "点击视频卡片进入详情页，即可查看简介、收藏并发现相关推荐。" },
    { step: "04", title: "收藏分享", desc: "喜欢的视频可以一键收藏（保存在本地），也能复制链接分享给朋友。" },
  ];

  const stats = [
    { value: `${videos.length}+`, label: "演示视频" },
    { value: `${categories.length}`, label: "内容分类" },
    { value: formatViews(totalViews), label: "累计播放" },
  ];

  return (
    <div className="px-4 py-8 md:px-6 md:py-12">
      <section className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-label text-secondary-900">
          <i className="ri-information-line text-primary-500"></i>
          关于我们
        </span>
        <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
          关于日批视频
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
          日批视频是一个面向年轻互联网用户的短视频聚合平台，专注于收录最新、最热门的短视频内容。
          我们把分散在各个角落的有趣视频按分类整理，帮助你在碎片时间里高效发现喜欢的内容，
          用最简单的方式享受丰富多样的视频娱乐。
        </p>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3 md:max-w-2xl">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-heading text-2xl font-extrabold text-primary-600">{s.value}</p>
              <p className="mt-1 text-xs text-foreground-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          内容范围
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          覆盖多元类别，满足不同兴趣的观看需求
        </p>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 md:gap-4">
          {scopePoints.map((p) => (
            <div
              key={p.title}
              className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${p.icon} text-2xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          使用说明
        </h2>
        <p className="mt-2 text-sm text-foreground-500">四步找到你想看的短视频</p>
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 md:gap-4">
          {steps.map((s) => (
            <div key={s.step} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="font-heading text-2xl font-extrabold text-accent-500">{s.step}</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <div className="flex flex-col gap-6 rounded-lg border border-background-200 bg-accent-100 p-6 md:flex-row md:items-center md:justify-between md:p-10">
          <div className="max-w-2xl">
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              联系与反馈
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              如果你对内容有任何建议、发现内容问题，或希望推荐你喜欢的短视频，
              欢迎通过下方邮箱联系我们，我们会尽快回复。
            </p>
            <a
              href="mailto:contact@ripivideo.com"
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-sm font-label text-foreground-900 transition-colors duration-200 hover:text-primary-600"
            >
              <i className="ri-mail-line text-lg text-primary-500"></i>
              contact@ripivideo.com
            </a>
          </div>
          <Link
            to="/"
            className="inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <i className="ri-play-fill text-lg"></i>
            返回首页观看
          </Link>
        </div>
      </section>
    </div>
  );
}